import React, { ReactElement, isValidElement, useContext, useMemo } from 'react'

import { F0Checkbox } from '@factorialco/f0-react'

import { SummaryContext } from './SummaryContext'
import { Props as TimeBlockEntryProps } from './TimeBlockEntry'

type Props = {
  title: string
  totalHours: string
  children: React.ReactNode
}

const TimeBlockGroup = ({ title, totalHours, children }: Props) => {
  const { selectable, selectionManager } = useContext(SummaryContext)

  const entries = React.Children.toArray(children).filter(
    (child): child is ReactElement<TimeBlockEntryProps> => isValidElement(child)
  )

  const entryIds = useMemo(() => entries.map((child) => child.props.id), [entries])

  // Determine the state of the group checkbox using the selection manager
  const isAllSelected = selectionManager?.isGroupFullySelected(entryIds) ?? false
  const isSomeSelected = selectionManager?.isGroupPartiallySelected(entryIds) ?? false

  const handleSelectAll = () => {
    if (!selectionManager) return
    selectionManager.toggleGroup(entryIds)
  }

  const handleToggleEntry = (entryId: string) => {
    if (!selectionManager) return
    selectionManager.toggleEntry(entryId)
  }

  return (
    <div className="pt-4 first:pt-0">
      {/* Group Header */}
      <div className="flex justify-between font-semibold items-center mb-2 px-3">
        {selectable ? (
          <F0Checkbox
            indeterminate={isSomeSelected}
            checked={isAllSelected || isSomeSelected}
            onCheckedChange={handleSelectAll}
            title={title}
          />
        ) : (
          <span className="text-base">{title}</span>
        )}
        <span className="w-18 text-base text-right">{totalHours}</span>
      </div>

      <div className="flex flex-col border border-solid border-f1-border-secondary rounded-xl">
        {entries.map((child, index) =>
          React.cloneElement(child, {
            checked: selectionManager?.isSelected(child.props.id) ?? false,
            onToggle: handleToggleEntry,
            showSeparator: index !== entries.length - 1,
          })
        )}
      </div>
    </div>
  )
}

export default TimeBlockGroup
